import React from 'react';
import { MapPin, Calendar, DollarSign, Clock, MessageCircle, Eye, ChevronRight } from 'lucide-react';
import { Job } from '@/types';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusIndicator';

interface JobCardProps {
    job: Job;
    onView?: (jobId: string) => void;
    onMessage?: (jobId: string) => void;
    showQuoteCount?: boolean;
    compact?: boolean;
}

/**
 * Professional Job Card Component
 * Clean design for displaying job listings
 */
export const JobCard: React.FC<JobCardProps> = ({
    job,
    onView,
    onMessage,
    showQuoteCount = true,
    compact = false
}) => {
    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
            'PENDING': { variant: 'neutral', label: 'Pending' },
            'OPEN': { variant: 'info', label: 'Open' },
            'ACTIVE': { variant: 'info', label: 'Active' },
            'IN_PROGRESS': { variant: 'warning', label: 'In Progress' },
            'COMPLETED': { variant: 'success', label: 'Completed' },
            'CANCELLED': { variant: 'error', label: 'Cancelled' }
        };
        return statusMap[status] || { variant: 'neutral' as const, label: status };
    };

    const statusInfo = getStatusBadge(job.status);

    if (compact) {
        return (
            <Card hover padding="md" onClick={() => onView && onView(job.id)} className="cursor-pointer">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900 truncate">{job.title}</h4>
                            <StatusBadge 
                                variant={statusInfo.variant} 
                                label={statusInfo.label}
                                size="sm"
                            />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(job.createdAt).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
            </Card>
        );
    }

    return (
        <Card hover>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {job.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                        {job.category || 'General Service'}
                    </p>
                </div>
                <StatusBadge 
                    variant={statusInfo.variant} 
                    label={statusInfo.label}
                />
            </div>

            {/* Description */}
            {job.description && (
                <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                    {job.description}
                </p>
            )}

            {/* Job Details Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <div>
                        <div className="text-xs text-gray-500">Location</div>
                        <div className="font-medium text-gray-900">{job.location}</div>
                    </div>
                </div>

                {job.budgetRange && (
                    <div className="flex items-center gap-2 text-gray-600">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <div>
                            <div className="text-xs text-gray-500">Budget</div>
                            <div className="font-medium text-gray-900">
                                €{job.budgetRange.min}-€{job.budgetRange.max}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                        <div className="text-xs text-gray-500">Posted</div>
                        <div className="font-medium text-gray-900">
                            {new Date(job.createdAt).toLocaleDateString()}
                        </div>
                    </div>
                </div>

                {job.urgency && (
                    <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                            <div className="text-xs text-gray-500">Urgency</div>
                            <div className="font-medium text-gray-900 capitalize">{job.urgency}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Quote Count */}
            {showQuoteCount && job.quoteCount !== undefined && (
                <div className="mb-4 p-3 bg-primary-50 rounded-lg border border-primary-100">
                    <p className="text-sm font-medium text-primary-900">
                        {job.quoteCount === 0 ? 'No quotes yet' :
                            job.quoteCount === 1 ? '1 quote received' :
                                `${job.quoteCount} quotes received`}
                    </p>
                </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-gray-100">
                {onView && (
                    <Button
                        variant="primary"
                        fullWidth
                        onClick={() => onView(job.id)}
                        leftIcon={<Eye className="w-4 h-4" />}
                    >
                        View Details
                    </Button>
                )}
                {onMessage && (
                    <Button
                        variant="outline"
                        onClick={() => onMessage(job.id)}
                        leftIcon={<MessageCircle className="w-4 h-4" />}
                    >
                        Message
                    </Button>
                )}
            </div>
        </Card>
    );
};
